/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  SessionManagerService,
  type Session,
  type SessionState,
  type TCreateSessionParams
} from "@tivocorp/data-domain-lib";

import type { ISessionInteractor } from "../../interfaces/ISessionInteractor";
import type { TNetworkOptions } from "../../types/TNetworkOptions";
import { Log } from "../../logging/Log";
import { ApplicationFeatureArea } from "../../enums";
import { SESSION_MANAGER_API_VERSION, TIVO_SERVICES } from "../../../config";
import { BaseInteractor } from "../base";
import { ServiceLocator } from "../base/ServiceLocator";

/**
 * Shared, atomic interactor wrapping {@link SessionManagerService}.
 *
 * Single owner of every call to the session-manager service across the
 * app (linear/VOD/nDVR/SOCU streaming session impls, sign-in resume,
 * background keep-alive). The {@link ApplicationFeatureArea} is fixed
 * at construction time (defaults to HOME for parity with the legacy
 * call sites in `ChannelsInteractor`).
 *
 * Stateless: a fresh `SessionManagerService` is built per call via
 * {@link ServiceLocator}.
 */
export class SessionInteractor
  extends BaseInteractor
  implements ISessionInteractor
{
  private static readonly TAG: string = "SessionInteractor";

  public constructor(
    private readonly mFeatureArea: ApplicationFeatureArea = ApplicationFeatureArea.HOME
  ) {
    super();
  }

  public async createSession(
    sessionParams: TCreateSessionParams,
    options?: TNetworkOptions
  ): Promise<Session> {
    return this.executeGuarded(
      "createSession",
      async (guardedSignal: AbortSignal): Promise<Session> => {
        Log.d(SessionInteractor.TAG, "createSession() called");
        const service: SessionManagerService = await this.buildService();
        // Caller-supplied apiVersion wins over the configured default
        return service.createSession(
          { apiVersion: SESSION_MANAGER_API_VERSION, ...sessionParams },
          guardedSignal
        );
      },
      SessionInteractor.withLifecycleOptions(options)
    );
  }

  public async sessionKeepAlive(
    session: Session,
    options?: TNetworkOptions
  ): Promise<SessionState> {
    return this.executeGuarded(
      "sessionKeepAlive",
      async (guardedSignal: AbortSignal): Promise<SessionState> => {
        Log.d(SessionInteractor.TAG, "sessionKeepAlive() called");
        const service: SessionManagerService = await this.buildService();
        return service.sessionKeepAlive(session, guardedSignal);
      },
      SessionInteractor.withLifecycleOptions(options)
    );
  }

  public async sessionDelete(
    session: Session,
    options?: TNetworkOptions
  ): Promise<SessionState> {
    return this.executeGuarded(
      "sessionDelete",
      async (guardedSignal: AbortSignal): Promise<SessionState> => {
        Log.d(SessionInteractor.TAG, "sessionDelete() called");
        const service: SessionManagerService = await this.buildService();
        return service.sessionDelete(session, guardedSignal);
      },
      SessionInteractor.withLifecycleOptions(options)
    );
  }

  /**
   * Session calls outlive the screen that made them: the stream plays on
   * across navigation, so they are not cancelled with that screen's calls.
   * An aborted delete leaves the backend session open — counting against
   * the stream limit (TooManyStreams) until it expires — and an aborted
   * create or keep-alive fails a stream that is still on screen.
   */
  private static withLifecycleOptions(
    options?: TNetworkOptions
  ): TNetworkOptions {
    return { bypassNavigationCancellation: true, ...options };
  }

  private async buildService(): Promise<SessionManagerService> {
    return ServiceLocator.getInstance(
      SessionManagerService,
      await this.buildServicesParams(
        TIVO_SERVICES.SessionManagerService,
        this.mFeatureArea
      )
    );
  }
}
